import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Slider as UiSlider } from 'components/ui/slider';

interface MultiRangeSliderProps {
  min: number;
  max: number;
  step?: number;
  onChange: (range: { min: number; max: number }) => void;
}

const DEBOUNCE_DELAY = 75;

const MultiRangeSlider: FC<MultiRangeSliderProps> = ({
  min,
  max,
  step = 1,
  onChange,
}) => {
  const [values, setValues] = useState<[number, number]>([min, max]);
  const changeTimeoutRef = useRef<number | undefined>(undefined);

  const clearPendingChange = useCallback(() => {
    if (
      changeTimeoutRef.current !== undefined &&
      typeof window !== 'undefined'
    ) {
      window.clearTimeout(changeTimeoutRef.current);
    }
    changeTimeoutRef.current = undefined;
  }, []);

  const emitChange = useCallback(
    (rangeValues: [number, number], { debounce }: { debounce: boolean }) => {
      const trigger = () =>
        onChange({ min: rangeValues[0], max: rangeValues[1] });

      if (!debounce || typeof window === 'undefined') {
        clearPendingChange();
        trigger();
        return;
      }

      clearPendingChange();
      changeTimeoutRef.current = window.setTimeout(() => {
        trigger();
        changeTimeoutRef.current = undefined;
      }, DEBOUNCE_DELAY);
    },
    [clearPendingChange, onChange]
  );

  useEffect(() => {
    setValues([min, max]);
    clearPendingChange();
  }, [clearPendingChange, max, min]);

  useEffect(() => {
    return () => {
      clearPendingChange();
    };
  }, [clearPendingChange]);

  const minStepsBetweenThumbs = useMemo(() => {
    if (step <= 0) {
      return 1;
    }

    const minimumDistance = Math.max(1, Math.round(1 / step));
    const rangeSteps = Math.max(1, Math.round((max - min) / step));
    return Math.min(minimumDistance, rangeSteps);
  }, [max, min, step]);

  const handleValueChange = useCallback(
    (sliderValues: number[]) => {
      if (sliderValues.length < 2) {
        return;
      }

      const [nextMin, nextMax] = sliderValues;

      setValues((previous) => {
        if (previous[0] === nextMin && previous[1] === nextMax) {
          return previous;
        }

        emitChange([nextMin, nextMax], { debounce: true });
        return [nextMin, nextMax];
      });
    },
    [emitChange]
  );

  const handleValueCommit = useCallback(
    (sliderValues: number[]) => {
      if (sliderValues.length < 2) {
        return;
      }

      const [nextMin, nextMax] = sliderValues;
      setValues((previous) =>
        previous[0] === nextMin && previous[1] === nextMax
          ? previous
          : [nextMin, nextMax]
      );
      emitChange([nextMin, nextMax], { debounce: false });
    },
    [emitChange]
  );

  return (
    <div className="py-4" data-testid="multi-range-slider">
      <UiSlider
        value={values}
        onValueChange={handleValueChange}
        onValueCommit={handleValueCommit}
        min={min}
        max={max}
        step={step}
        minStepsBetweenThumbs={minStepsBetweenThumbs}
        trackClassName="h-3"
        rangeClassName="bg-primary"
        thumbClassName="h-5 w-5 border-primary"
        thumbs={[
          {
            'aria-label': 'Minimum value',
            'data-testid': 'min-value-slider',
          } as any,
          {
            'aria-label': 'Maximum value',
            'data-testid': 'max-value-slider',
          } as any,
        ]}
      />
      <div className="mt-4 flex justify-between text-sm text-gray-600">
        <span data-testid="min-value-display">{values[0]}</span>
        <span data-testid="max-value-display">{values[1]}</span>
      </div>
    </div>
  );
};

export default MultiRangeSlider;
